"use client";

import { useCallback, useSyncExternalStore } from "react";
import type { Omittable } from "../lib/props";

const BREAKPOINTS = {
  "2xl": 1536,
  "3xl": 1600,
  "4xl": 2000,
  lg: 1024,
  md: 800,
  sm: 640,
  xl: 1280,
} as const;

type Breakpoint = keyof typeof BREAKPOINTS;

type BreakpointQuery =
  | Breakpoint
  | `max-${Breakpoint}`
  | `${Breakpoint}:max-${Breakpoint}`;

function resolveMin(value: Breakpoint | number): string {
  const px = typeof value === "number" ? value : BREAKPOINTS[value];
  return `(min-width: ${px}px)`;
}

function resolveMax(value: Breakpoint | number): string {
  const px = typeof value === "number" ? value : BREAKPOINTS[value];
  return `(max-width: ${px - 1}px)`;
}

function parseQuery(
  query: BreakpointQuery | MediaQueryInput | (string & {}),
): string {
  if (typeof query !== "string") {
    const parts: string[] = [];
    if (query.min != null) parts.push(resolveMin(query.min));
    if (query.max != null) parts.push(resolveMax(query.max));
    if (query.pointer === "coarse") parts.push("(pointer: coarse)");
    if (query.pointer === "fine") parts.push("(pointer: fine)");
    if (parts.length === 0) return "(min-width: 0px)";
    return parts.join(" and ");
  }

  if (query.startsWith("(")) return query;

  const parts: string[] = [];
  for (const segment of query.split(":")) {
    if (segment.startsWith("max-")) {
      const bp = segment.slice(4);
      if (bp in BREAKPOINTS) parts.push(resolveMax(bp as Breakpoint));
    } else if (segment in BREAKPOINTS) {
      parts.push(resolveMin(segment as Breakpoint));
    }
  }

  return parts.length > 0 ? parts.join(" and ") : query;
}

// A `MediaQueryList` is a live object the browser registers against the style
// engine, and `parseQuery` walks the string. Neither may run inside
// `getSnapshot`, which `useSyncExternalStore` calls on EVERY render — a
// screen with hundreds of `useIsMobile()` callers (one per entity chip in a
// table row) constructed hundreds of MediaQueryLists per render pass. The app
// only ever uses a handful of distinct queries, so both results are cached by
// query string and the hot path is reduced to one Map lookup plus a `.matches`
// read.
const parsedQueryCache = new Map<string, string>();
const mediaQueryListCache = new Map<string, MediaQueryList>();

function parseQueryCached(
  query: BreakpointQuery | MediaQueryInput | (string & {}),
): string {
  if (typeof query !== "string") return parseQuery(query);
  const cached = parsedQueryCache.get(query);
  if (cached !== undefined) return cached;
  const parsed = parseQuery(query);
  parsedQueryCache.set(query, parsed);
  return parsed;
}

function getMediaQueryList(mediaQuery: string): MediaQueryList {
  let mql = mediaQueryListCache.get(mediaQuery);
  if (mql === undefined) {
    mql = window.matchMedia(mediaQuery);
    mediaQueryListCache.set(mediaQuery, mql);
  }
  return mql;
}

export type MediaQueryInput = Omittable<{
  min?: Breakpoint | number;
  max?: Breakpoint | number;
  /** Touch-like input (finger). Use "fine" for mouse/trackpad. */
  pointer?: "coarse" | "fine";
}>;

export function useMediaQuery(
  query: BreakpointQuery | MediaQueryInput | (string & {}),
): boolean {
  const mediaQuery = parseQueryCached(query);

  const subscribe = useCallback(
    (callback: () => void) => {
      const mql = getMediaQueryList(mediaQuery);
      mql.addEventListener("change", callback);
      return () => mql.removeEventListener("change", callback);
    },
    [mediaQuery],
  );

  const getSnapshot = useCallback(
    () => getMediaQueryList(mediaQuery).matches,
    [mediaQuery],
  );

  // The server has no viewport; `false` renders the wide/default layout and
  // hydration corrects it. Without a server snapshot React throws during SSR.
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

function getServerSnapshot(): boolean {
  return false;
}

/**
 * The shell's width ladder — ONE scale for JS and CSS alike.
 *
 *  - `compact` (< 640): phones. Navigation folds into the left drawer.
 *  - `medium` (640–1279): every iPad (both orientations) and small desktop
 *    windows. The icon rail stays pinned; the module-menu pane presents as an
 *    overlay flyout instead of a permanent column.
 *  - `full` (≥ 1280): the pinned-pane desktop shell.
 *
 * The compact cut is Tailwind's `sm`, so the shell flips where the dialogs
 * already bottom-sheet (`max-sm:`) — one "mobile" instead of three. The full
 * cut is `xl`, chosen above the widest iPad landscape (1194).
 */
export type ShellTier = "compact" | "medium" | "full";

export function useShellTier(): ShellTier {
  const compact = useMediaQuery("max-sm");
  const full = useMediaQuery("xl");
  // The server has no viewport: both queries snapshot false, so SSR renders
  // `medium` and hydration corrects it — the middle tier is the cheapest
  // wrong guess in either direction.
  if (compact) return "compact";
  return full ? "full" : "medium";
}

/** True in the shell's `compact` tier — see {@link useShellTier}. */
export function useIsMobile(): boolean {
  return useMediaQuery("max-sm");
}
